// src/components/post/CommentForm.jsx
import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaPaperPlane, FaImage, FaTimes } from 'react-icons/fa';
import { UserContext } from '../../context/userContext';

const CommentForm = ({ postId, parentCommentId = null, onCommentAdded }) => {
  const [text, setText] = useState('');
  const [media, setMedia] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { currentUser } = useContext(UserContext);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentUser) {
      navigate('/login');
      return;
    }

    if (!text.trim() && !media) {
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('postId', postId);
      formData.append('text', text);
      
      if (parentCommentId) {
        formData.append('parentCommentId', parentCommentId);
      }
      
      if (media) {
        formData.append('media', media);
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/comments`,
        formData,
        {
          headers: { 
            Authorization: `Bearer ${currentUser.token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Clear form
      setText('');
      setMedia(null);
      
      // Notify parent component
      if (onCommentAdded) {
        onCommentAdded(response.data);
      }
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // If not logged in, show prompt to login
  if (!currentUser) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 text-center">
        <p className="text-gray-600 mb-3">Please log in to join the conversation</p>
        <button 
          onClick={() => navigate('/login')}
          className="px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90 transition-colors"
        >
          Log in
        </button>
      </div>
    );
  }

  return (
    <form className="bg-white rounded-lg shadow-sm overflow-hidden" onSubmit={handleSubmit}>
      <div className="p-4">
        <textarea
          className="w-full border-0 focus:ring-0 resize-none bg-transparent outline-none text-gray-700 placeholder-gray-400"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={parentCommentId ? "Write a reply..." : "Write a comment..."}
          disabled={isSubmitting}
          rows={media ? 3 : 2}
        />
        
        {media && (
          <div className="relative mt-2 rounded-md overflow-hidden bg-gray-100 inline-block">
            <img 
              src={URL.createObjectURL(media)} 
              alt="Media preview" 
              className="max-h-40 max-w-full object-contain"
            />
            <button
              type="button"
              onClick={() => setMedia(null)}
              className="absolute top-1 right-1 bg-gray-800 bg-opacity-70 text-white rounded-full p-1 hover:bg-opacity-100 transition-opacity"
            >
              <FaTimes size={14} />
            </button>
          </div>
        )}
      </div>
      
      <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100">
        <label className="cursor-pointer text-gray-500 hover:text-primary transition-colors">
          <FaImage />
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setMedia(e.target.files[0])}
            disabled={isSubmitting}
            className="hidden"
          />
        </label>
        
        <button 
          type="submit" 
          className={`flex items-center gap-2 px-4 py-1 rounded-full text-sm font-medium ${
            isSubmitting || (!text.trim() && !media)
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-primary text-white hover:bg-opacity-90 transition-colors'
          }`}
          disabled={isSubmitting || (!text.trim() && !media)}
        >
          {isSubmitting ? 'Posting...' : 'Post'}
          <FaPaperPlane size={12} />
        </button>
      </div>
    </form>
  );
};

export default CommentForm;